import React, { useState, useCallback } from 'react';
import useOverlayFocus from './useOverlayFocus';

// Data for the 6 healing sounds and movements
const healingSoundsData = [
    { 
        name: 'XU (嘘)', 
        icon: 'fas fa-tree', 
        color: 'text-green-400', 
        organ: 'Játra', 
        description: 'Zvuk pro játra a žlučník. Pomáhá uvolnit hněv a napětí.',
        longDescription: 'XU (嘘) - Zvuk pro játra\n\nOrgan: Játra a žlučník\nElement: Dřevo\nEmoce: Hněv, frustrace\nRoční období: Jaro\nBarva: Zelená\n\nZvuk XU se vyslovuje jako jemné "šú" s lehkým výdechem. Tento zvuk pomáhá:\n- Uvolnit napětí v játrech\n- Zklidnit hněv a podráždění\n- Zlepšit trávení tuků\n- Podpořit detoxikaci těla\n- Harmonizovat emoce\n\nPostup:\n1. Seďte nebo stůjte v klidné pozici\n2. Položte ruce na žebra pod prsa\n3. Při výdechu pomalu vyslovujte "XU"\n4. Představujte si zelenou barvu proudící játry\n5. Opakujte 6x',
        hasVideo: true,
        videoUrl: require('./videos/jatra.mp4'),
    },
    { 
        name: 'HE (呵)', 
        icon: 'fas fa-heart', 
        color: 'text-red-400', 
        organ: 'Srdce', 
        description: 'Zvuk pro srdce a tenké střevo. Podporuje radost a klid mysli.',
        longDescription: 'HE (呵) - Zvuk pro srdce\n\nOrgan: Srdce a tenké střevo\nElement: Oheň\nEmoce: Přehnaná radost, agitovanost\nRoční období: Léto\nBarva: Červená\n\nZvuk HE se vyslovuje jako "che" s otevřenou pusou. Tento zvuk pomáhá:\n- Uklidnit srdce a mysl\n- Zlepšit krevní oběh\n- Harmonizovat emoce\n- Snížit stres a úzkost\n- Podpořit jasnost mysli\n\nPostup:\n1. Seďte vzpřímeně s rukama na srdci\n2. Při výdechu otevřete ústa a řekněte "HE"\n3. Představujte si červené světlo v oblasti srdce\n4. Cítěte teplo a klid v hrudi\n5. Opakujte 6x',
        hasVideo: true,
        videoUrl: require('./videos/srdce.mp4'),
    },
    { 
        name: 'HU (呼)', 
        icon: 'fas fa-globe-asia', 
        color: 'text-yellow-400', 
        organ: 'Slezina', 
        description: 'Zvuk pro slezinu a žaludek. Posiluje trávení a stabilitu.',
        longDescription: 'HU (呼) - Zvuk pro slezinu\n\nOrgan: Slezina a žaludek\nElement: Země\nEmoce: Přemýšlení, starosti\nRoční období: Pozdní léto\nBarva: Žlutá\n\nZvuk HU se vyslovuje jako hluboké "chu". Tento zvuk pomáhá:\n- Posílit trávicí systém\n- Uklidnit mysl od starostí\n- Zlepšit vstřebávání živin\n- Stabilizovat emoce\n- Podpořit energii celého těla\n\nPostup:\n1. Položte ruce na oblast žaludku\n2. Při výdechu vyslovujte hluboké "HU"\n3. Představujte si žluté světlo v oblasti břicha\n4. Cítěte teplo a stabilitu ve středu těla\n5. Opakujte 6x',
        hasVideo: true,
        videoUrl: require('./videos/slezina.mp4'),
        photos: [
            'https://placehold.co/400x300/EAB308/FFFFFF?text=HU+%E5%91%BC',
            'https://placehold.co/400x300/CA8A04/FFFFFF?text=Slezina+%E8%84%BE',
            'https://placehold.co/400x300/A16207/FFFFFF?text=Zem%C4%9B+%E5%9C%9F'
        ]
    },
    { 
        name: 'SI (嘶)', 
        icon: 'fas fa-wind', 
        color: 'text-gray-300', 
        organ: 'Plíce', 
        description: 'Zvuk pro plíce a tlusté střevo. Podporuje dýchání a uvolnění.',
        longDescription: 'SI (嘶) - Zvuk pro plíce\n\nOrgan: Plíce a tlusté střevo\nElement: Kov\nEmoce: Smutek, žal\nRoční období: Podzim\nBarva: Bílá\n\nZvuk SI se vyslovuje jako sykavé "s" s částečně otevřenými ústy. Tento zvuk pomáhá:\n- Posílit dýchací systém\n- Uvolnit smutek a žal\n- Zlepšit funkci plic\n- Podpořit imunitní systém\n- Harmonizovat dech\n\nPostup:\n1. Položte ruce na hrudník\n2. Při výdechu sykavě vyslovujte "SI"\n3. Představujte si bílé světlo v plicích\n4. Cítěte, jak se plíce rozšiřují a čistí\n5. Opakujte 6x',
        hasVideo: true,
        videoUrl: require('./videos/plice.mp4'),
        photos: [
            'https://placehold.co/400x300/9CA3AF/FFFFFF?text=SI+%E5%98%B6',
            'https://placehold.co/400x300/6B7280/FFFFFF?text=Pl%C3%ADce+%E8%82%BA',
            'https://placehold.co/400x300/4B5563/FFFFFF?text=Kov+%E9%87%91'
        ]
    },
    { 
        name: 'CHUI (吹)', 
        icon: 'fas fa-tint', 
        color: 'text-blue-400', 
        organ: 'Ledviny', 
        description: 'Zvuk pro ledviny a močový měchýř. Posiluje životní energii.',
        longDescription: 'CHUI (吹) - Zvuk pro ledviny\n\nOrgan: Ledviny a močový měchýř\nElement: Voda\nEmoce: Strach, úzkost\nRoční období: Zima\nBarva: Černá/tmavě modrá\n\nZvuk CHUI se vyslovuje jako "čuej" s kulatými rty. Tento zvuk pomáhá:\n- Posílit ledviny a nadledviny\n- Překonat strach a úzkost\n- Zvýšit životní energii\n- Zlepšit funkci močového systému\n- Podpořit reprodukční zdraví\n\nPostup:\n1. Položte ruce na ledviny (záda v oblasti pasu)\n2. Při výdechu vyslovujte "CHUI"\n3. Představujte si tmavě modré světlo v ledvinách\n4. Cítěte sílu a stabilitu v dolní části těla\n5. Opakujte 6x',
        hasVideo: true,
        // videoUrl: require('./videos/ledviny.mp4'),
    },
    { 
        name: 'XI (嘻)', 
        icon: 'fas fa-fire', 
        color: 'text-purple-400', 
        organ: 'Trojitý ohřívač', 
        description: 'Zvuk pro trojitý ohřívač. Harmonizuje celý energetický systém.',
        longDescription: 'XI (嘻) - Zvuk pro trojitý ohřívač\n\nOrgan: Trojitý ohřívač (San Jiao)\nElement: Koordinuje všechny elementy\nFunkce: Regulace teploty a energie\nÚčinek: Celková harmonizace\n\nZvuk XI se vyslovuje jako radostné "chí" s úsměvem. Tento zvuk pomáhá:\n- Harmonizovat všechny orgánové systémy\n- Regulovat tělesnou teplotu\n- Zlepšit cirkulaci qi\n- Podpořit celkovou vitalitu\n- Sladit všechny orgány mezi sebou\n\nPostup:\n1. Postavte se do pozice s rukama nad hlavou\n2. Při výdechu se usmějte a vyslovujte "XI"\n3. Představujte si zlaté světlo proudící celým tělem\n4. Cítěte harmonii a rovnováhu ve všech orgánech\n5. Opakujte 6x\n\nTrojitý ohřívač není fyzický orgán, ale energetická funkce, která koordinuje horní, střední a dolní část těla.',
        hasVideo: true,
        // videoUrl: require('./videos/ohrivac.mp4'),
    }
];

const HealingSounds = () => {
    // State for selected healing sound
    const [selectedSound, setSelectedSound] = useState(null);
    // State for modal visibility
    const [isModalOpen, setIsModalOpen] = useState(false);
    // State for current image index in gallery
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Function to open modal and set selected sound
    const openModal = (sound) => {
        setSelectedSound(sound);
        setCurrentImageIndex(0);
        setIsModalOpen(true);
    };

    // Function to close modal
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedSound(null);
    };

    const overlayRef = useOverlayFocus(isModalOpen, closeModal);

    const photoCount = selectedSound?.photos?.length || 1;
    const nextImage = useCallback(() => setCurrentImageIndex(n => (n + 1) % photoCount), [photoCount]);
    const prevImage = useCallback(() => setCurrentImageIndex(n => (n - 1 + photoCount) % photoCount), [photoCount]);

    // Keyboard event handler for modal
    React.useEffect(() => {
        const handleKeyDown = (event) => {
            if (!isModalOpen) return;
            
            switch(event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    prevImage();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    nextImage();
                    break;
                default:
                    break;
            }
        };

        if (isModalOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isModalOpen, nextImage, prevImage]);

    // Function for gallery navigation forward
    return (
        <section id="healing-sounds" className="py-20 relative overflow-hidden text-gray-200">
            {/* Background image with Parallax effect */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url('https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')` }}
            ></div>

            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black opacity-75"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold mb-4">6 léčivých zvuků a pohybů</h2>
                    <div className="w-24 h-1 bg-yellow-600 mx-auto"></div>
                    <p className="text-xl mt-6 max-w-3xl mx-auto">Tradiční taoistická technika pro harmonizaci vnitřních orgánů pomocí léčivých zvuků a pohybů</p>
                </div>

                <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
                    {healingSoundsData.map((sound, index) => (
                        <div 
                            key={index}
                            className="element-card glass-effect rounded-xl overflow-hidden shadow-md cursor-pointer transition-all duration-700 hover:scale-105 hover:shadow-2xl group relative"
                            role="button" tabIndex={0} aria-label={`Více informací: ${sound.name}`}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(sound); } }}
                            onClick={() => openModal(sound)}
                        >
                            <div className="h-48 flex flex-col items-center justify-center bg-black bg-opacity-20 relative overflow-hidden p-4">
                                <i aria-hidden="true" className={`${sound.icon} text-4xl ${sound.color} transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 mb-2`}></i>
                                
                                {/* Chinese character or organ name */}
                                <div className="text-center">
                                    <h4 className="text-lg font-bold text-white mb-1">{sound.name}</h4>
                                    <p className="text-sm text-gray-300">{sound.organ}</p>
                                </div>
                                
                                {/* Video indicator for sounds with videos */}
                                {sound.hasVideo && (
                                    <div className="absolute top-3 left-3 bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center animate-pulse">
                                        <i aria-hidden="true" className="fas fa-play text-xs"></i>
                                    </div>
                                )}
                                
                                {/* Enhanced hover indicators */}
                                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                                    <div className="bg-gold-text text-black rounded-full w-8 h-8 flex items-center justify-center animate-bounce-gentle">
                                        <i aria-hidden="true" className="fas fa-expand-alt text-xs"></i>
                                    </div>
                                </div>

                                {/* Animated background particles */}
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    {[...Array(6)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="absolute w-1 h-1 bg-gold-text rounded-full animate-float"
                                            style={{
                                                left: `${20 + Math.random() * 60}%`,
                                                top: `${20 + Math.random() * 60}%`,
                                                animationDelay: `${Math.random() * 2}s`,
                                                animationDuration: `${2 + Math.random() * 1}s`,
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Gradient overlay on hover */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            </div>
                            <div className="p-4 relative">
                                <p className="text-gray-300 group-hover:text-gray-200 transition-colors duration-300 animate-fade-in-up animate-delay-100 text-sm">
                                    {sound.description}
                                </p>
                                
                                {/* Enhanced interaction indicator */}
                                <div className="mt-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                                    <span className="text-xs text-gold-text flex items-center animate-bounce-gentle">
                                        <i aria-hidden="true" className="fas fa-mouse-pointer mr-1 animate-pulse"></i>
                                        Více informací
                                        <i aria-hidden="true" className="fas fa-arrow-right ml-1 transform group-hover:translate-x-1 transition-transform duration-300"></i>
                                    </span>
                                </div>
                            </div>

                            {/* Enhanced border effect */}
                            <div className="absolute inset-0 border-2 border-gold-text opacity-0 group-hover:opacity-50 rounded-xl transition-all duration-500 pointer-events-none animate-pulse-glow"></div>
                            
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold-text/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            </div>
                        </div>
                    ))}
                </div>

                <details className="practice-details"><summary>Jak praktikovat šest léčivých zvuků<span aria-hidden="true">＋</span></summary><div className="practice-details-content">
                    <div className="text-center mb-8">
                        <h3 className="text-3xl font-bold mb-2 gold-text">6 léčivých zvuků</h3>
                        <h4 className="text-2xl font-semibold mb-6">Liu Zi Jue (六字訣)</h4>
                        <div className="w-24 h-1 gold-bg mx-auto mb-8"></div>
                    </div>
                    
                    <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-300 leading-relaxed">
                        <p>Šest léčivých zvuků (Liu Zi Jue) je tradiční taoistická technika, která využívá léčivé zvuky v kombinaci s pohyby a dýcháním k harmonizaci vnitřních orgánů a jejich přiřazených emocí. Každý zvuk rezonuje s určitým orgánem a pomáhá uvolnit napětí, zlepšit funkci orgánů a obnovit energetickou rovnováhu.</p>
                        
                        <div className="grid md:grid-cols-2 gap-8 mt-10">
                            <div className="glass-effect p-6 rounded-lg">
                                <h5 className="text-xl font-bold mb-3 text-yellow-400">Principy praxe</h5>
                                <ul className="space-y-2 text-gray-300">
                                    <li>• Každý zvuk se opakuje 6x</li>
                                    <li>• Koordinace s dechem a pohybem</li>
                                    <li>• Vizualizace příslušné barvy</li>
                                    <li>• Soustředění na cílový orgán</li>
                                    <li>• Klidná a soustředěná mysl</li>
                                </ul>
                            </div>
                            
                            <div className="glass-effect p-6 rounded-lg">
                                <h5 className="text-xl font-bold mb-3 text-yellow-400">Benefity praxe</h5>
                                <ul className="space-y-2 text-gray-300">
                                    <li>• Harmonizace vnitřních orgánů</li>
                                    <li>• Uvolnění emocionálního napětí</li>
                                    <li>• Zlepšení dýchání a cirkulace</li>
                                    <li>• Posílení imunitního systému</li>
                                    <li>• Celková energetická rovnováha</li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="mt-10 text-center">
                            <h4 className="text-2xl font-bold mb-4 gold-text">Pokyny pro praxi</h4>
                            <p className="mb-4">Doporučuje se praktikovat ráno nebo večer v klidném prostředí. Začněte pomalu a postupně si zvykejte na jednotlivé zvuky. Nejlepší je procvičovat všech šest zvuků v pořadí, ale můžete se také zaměřit na konkrétní zvuk podle aktuálních potřeb.</p>
                            
                            <p className="mb-6">Pamatujte, že kvalita je důležitější než kvantita. Lépe je udělat každý zvuk 3x s plnou pozorností než 6x roztržitě.</p>
                            
                            <p className="text-gold-text font-semibold italic">Postupujte v pořadí: XU → HE → HU → SI → CHUI → XI</p>
                        </div>
                    </div>
                </div></details>
            </div>

            {/* Enhanced Modal window for displaying healing sound details */}
            {isModalOpen && selectedSound && (
                <div 
                    ref={overlayRef} role="dialog" aria-modal="true" aria-label={selectedSound?.name} tabIndex={-1}
                    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fade-in-up backdrop-blur-lg"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            closeModal();
                        }
                    }}
                >
                    <div className="glass-effect bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] relative animate-scale-in shadow-2xl border border-gold-text/30 flex flex-col"
                         onClick={(e) => e.stopPropagation()}
                    >
                        {/* Enhanced close button */}
                        <button 
                            aria-label="Zavřít detail" onClick={closeModal} 
                            className="absolute top-6 right-6 text-white text-2xl font-bold transition-all duration-300 hover:gold-text w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 z-10 hover:scale-110"
                        >
                            <svg 
                                className="w-6 h-6" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M6 18L18 6M6 6l12 12" 
                                />
                            </svg>
                        </button>
                        
                        {/* Header - fixed */}
                        <div className="text-center p-8 pb-4 animate-fade-in-up flex-shrink-0">
                            <h2 className="text-4xl font-bold mb-2 gold-text animate-pulse-glow">{selectedSound.name}</h2>
                            <p className="text-xl text-gray-300 mb-4">{selectedSound.organ}</p>
                            <div className="w-32 h-1 gold-bg mx-auto rounded animate-shimmer"></div>
                        </div>
                        
                        {/* Scrollable content */}
                        <div className="flex-1 overflow-y-auto px-8 pb-8 custom-scrollbar" style={{scrollBehavior: 'smooth'}}>
                            <div className="flex flex-col gap-8">
                                {/* Text content */}
                                <div className="animate-fade-in-left animate-delay-200">
                                    <p className="text-lg mb-4 text-gray-300 whitespace-pre-line leading-relaxed">
                                        {selectedSound.longDescription}
                                    </p>
                                </div>

                                {/* Enhanced photo gallery or video */}
                                {selectedSound.hasVideo ? (
                                    // Video section for sounds with videos
                                    <div className="relative flex justify-center items-center animate-fade-in-up animate-delay-400">
                                        <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-black/20 p-4">
                                            <h4 className="text-xl font-bold mb-4 text-center gold-text flex items-center justify-center">
                                                <i aria-hidden="true" className="fas fa-play-circle mr-2"></i>
                                                Ukázka cvičení zvuku {selectedSound.name}
                                            </h4>
                                            <video 
                                                src={selectedSound.videoUrl}
                                                controls
                                                className="w-64 h-auto object-contain rounded-xl shadow-lg transition-all duration-500 hover:scale-105"
                                                poster="" 
                                                preload="metadata"
                                                onError={(e) => {
                                                    console.error('Video failed to load:', selectedSound.videoUrl);
                                                    e.target.style.display = 'none';
                                                    const errorMsg = e.target.parentNode.querySelector('.video-error-message');
                                                    if (errorMsg) errorMsg.style.display = 'block';
                                                }}
                                            >
                                                <p className="text-gray-300 text-center">
                                                    Váš prohlížeč nepodporuje přehrávání videa.
                                                </p>
                                            </video>
                                            
                                            {/* Error message for failed video loading */}
                                            <div className="video-error-message hidden text-center p-4 bg-gray-700 rounded-xl">
                                                <i aria-hidden="true" className="fas fa-exclamation-triangle text-yellow-500 text-2xl mb-2"></i>
                                                <p className="text-gray-300 mb-2">Video se nepodařilo načíst</p>
                                                <p className="text-gray-400 text-sm">Prosím nahrajte video soubor pomocí admin panelu</p>
                                            </div>
                                            
                                            {/* Video overlay gradient */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none rounded-2xl"></div>
                                            
                                            {/* Decorative border */}
                                            <div className="absolute inset-0 border-2 border-gold-text/20 rounded-2xl pointer-events-none"></div>
                                        </div>
                                    </div>
                                ) : selectedSound.photos ? (
                                    // Photo gallery for sounds without videos but with photos
                                    <div className="relative flex justify-center items-center animate-fade-in-right animate-delay-400">
                                        <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                                            <img 
                                                src={selectedSound.photos[currentImageIndex]} 
                                                alt={`${selectedSound.name} obrázek ${currentImageIndex + 1}`} 
                                                className="w-72 h-auto object-contain transition-all duration-500 hover:scale-105" 
                                            />
                                            
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                                        </div>
                                        
                                        {/* Navigation buttons */}
                                        <button 
                                            aria-label="Předchozí fotografie" onClick={prevImage}
                                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-gold-text/80 text-white hover:text-black p-3 rounded-full hover:scale-110 transition-all duration-300 backdrop-blur-sm animate-bounce-gentle"
                                        >
                                            <i aria-hidden="true" className="fas fa-chevron-left text-lg"></i>
                                        </button>
                                        <button 
                                            aria-label="Další fotografie" onClick={nextImage}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-gold-text/80 text-white hover:text-black p-3 rounded-full hover:scale-110 transition-all duration-300 backdrop-blur-sm animate-bounce-gentle"
                                        >
                                            <i aria-hidden="true" className="fas fa-chevron-right text-lg"></i>
                                        </button>
                                        
                                        {/* Image counter */}
                                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm border border-gold-text/30 animate-pulse-glow">
                                            <span className="gold-text font-bold">{currentImageIndex + 1}</span> / {selectedSound.photos.length}
                                        </div>

                                        {/* Image dots indicator */}
                                        <div className="absolute bottom-4 right-4 flex space-x-2">
                                            {selectedSound.photos.map((_, index) => (
                                                <button
                                                    key={index}
                                                    aria-label={`Fotografie ${index + 1}`} onClick={() => setCurrentImageIndex(index)}
                                                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                                        index === currentImageIndex 
                                                            ? 'bg-gold-text scale-125' 
                                                            : 'bg-white/40 hover:bg-white/60'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    // Message for sounds without videos or photos
                                    <div className="text-center py-8 animate-fade-in-up animate-delay-400">
                                        <div className="glass-effect p-8 rounded-xl max-w-md mx-auto">
                                            <i aria-hidden="true" className="fas fa-music text-4xl text-gold-text mb-4"></i>
                                            <h4 className="text-xl font-bold mb-3 gold-text">Zvuk {selectedSound.name}</h4>
                                            <p className="text-gray-300 mb-4">
                                                Soustřeďte se na popis a techniku výše pro správné provedení tohoto léčivého zvuku.
                                            </p>
                                            <div className="text-sm text-gray-400 italic">
                                                Video demonstrace bude přidána v budoucnu
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Decorative elements */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold-text to-transparent animate-shimmer"></div>
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold-text to-transparent animate-shimmer"></div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default HealingSounds;